import { SubscriptionStatus, UserSubscriptionPlan } from "../constants/GlobalTypesAndEnumsAndConstants";

export type SubscriptionEntitlementOptions = {
  subscription?: {
    current_period_end_date?: unknown;
    subscription_resume_pending_payment?: unknown;
    creator_comped_elite?: unknown;
    subscription_id?: unknown;
    manager_subscription_account?: unknown;
    subscription_pause_paid_period_verification_required?: unknown;
  };
  now?: Date;
  requireActiveStatus?: boolean;
};

export function subscriptionTimestampMilliseconds(value: unknown): number | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value && typeof value === "object") {
    const timestamp = value as Record<string, unknown>;
    const seconds = timestamp.seconds ?? timestamp._seconds;
    const nanos = timestamp.nanoseconds ?? timestamp._nanoseconds ?? 0;
    if (typeof seconds === "number" && Number.isFinite(seconds) && typeof nanos === "number" && Number.isFinite(nanos)) {
      const milliseconds = seconds * 1000 + nanos / 1_000_000;
      return Number.isFinite(new Date(milliseconds).getTime()) ? milliseconds : null;
    }
  }
  return null;
}

/** Strict status rollout stays OFF until legacy paid-term reconciliation qualifies. */
export function hasActivePaidEntitlement(
  subscriptionStatus: unknown,
  subscriptionPlan: unknown,
  { subscription, now = new Date(), requireActiveStatus = import.meta.env.VITE_SUBSCRIPTION_ACTIVE_STATUS_ONLY === "true" }: SubscriptionEntitlementOptions = {}
): boolean {
  if (!subscriptionStatus || subscriptionStatus === SubscriptionStatus.CANCELED ||
    typeof subscriptionPlan !== "string" || !subscriptionPlan || subscriptionPlan === UserSubscriptionPlan.FREE) return false;
  // Only the current active Elite grant independently retains creator access.
  if (subscriptionStatus === SubscriptionStatus.ACTIVE && subscriptionPlan === UserSubscriptionPlan.ELITE && subscription?.creator_comped_elite === true) return true;
  if (subscription?.subscription_resume_pending_payment) return false;
  const periodEnd = subscriptionTimestampMilliseconds(subscription?.current_period_end_date);
  // An exam pass never renews: its end date is the whole entitlement, even before the Worker's expiry job has
  // written Free back (the Worker's effectiveSubscriptionPlan has the same rule)
  if (subscription?.manager_subscription_account === "stripe_exam_pass" && (periodEnd === null || periodEnd <= now.getTime())) return false;
  const hasVerifiedStripePauseTerm = subscription?.subscription_pause_paid_period_verification_required === true &&
    typeof subscription.subscription_id === "string" && subscription.subscription_id.trim().length > 0 &&
    (subscription.manager_subscription_account ?? "stripe") === "stripe";
  if ((subscriptionStatus === SubscriptionStatus.PAUSED || hasVerifiedStripePauseTerm) && periodEnd !== null && periodEnd <= now.getTime()) return false;
  return !requireActiveStatus || subscriptionStatus === SubscriptionStatus.ACTIVE;
}
